import { EventEmitter, Inject, Injectable } from '@angular/core';
import { User } from 'pdauth';
import { Subject } from 'rxjs';

import { Api } from '../api/api';
import { IHttp2RequestConfig } from '../api/http2-factory';
import { IColumnMapping, IColumnResult, IFilePreprocessResult, IFileProcessResult, IProcessReport, IProject, IUploadInfo } from '../components/petl/petl-types';
import { environment } from '../environment';
import { IAuthenticationService } from '../services/interfaces/IAuthenticationService';
import { LayerService } from '../services/layer.service';
import { ILayerColumn, ILayerSettings, IMapcoreProps, IMCMapView, IQuery } from '../types';

@Injectable({
    providedIn: 'root'
})
export class PetlService {

    columnData: IColumnResult[];
    columnHeadings: string[];
    columnHeadingsValidated: string;
    columnMapping: IColumnMapping;
    currentView: string = 'start';
    currentViewChange: Subject<any> = new Subject<any>();
    customTypes: string[] = ['char', 'num', 'date', 'url'];
    directiveTemplateBasePath: string = '';
    errorMessage: string;
    fileDropped: boolean;
    fileExtension: any = null;
    fileId: number;
    fileName: string;
    fileSample: string[][];
    fileUploaded: EventEmitter<IFilePreprocessResult> = new EventEmitter();
    ignorePreviousColumnMappings: boolean;
    initialized: boolean;
    loading?: boolean = null;
    mappingsFromServerAltered: boolean;
    nonNormalViews: string[] = ['about', 'manage-projects'];
    petlClose: EventEmitter<boolean>;
    complete: EventEmitter<IProject>;
    petlConfig: any;
    preProcessResult: IFilePreprocessResult;
    previousView: string = '';
    project: IProject;
    projectList: IProject[];
    promoteDataFinished: boolean;
    promoteDataSubmitted: boolean;
    promoteErrorOccurred: boolean;
    resultContainedPreviousMapping: boolean;
    results: IFilePreprocessResult;
    sheetCount: number;
    sheetNumber: number;
    showColumnMapping: boolean;
    showHelp: boolean = false;
    showRepeatFileHeaderIndicator: boolean;
    showRepeatFileNameIndicator: boolean;
    showResultsStats: boolean;
    startingRowNumber: number;
    user: User;
    validationSubmitted: boolean;
    validationReport: IProcessReport;
    validationResults: any;
    visible: boolean;
    wasVisible: boolean;
    zoomToLayer: boolean;

    // List of restricted aliases, since they can conflict with the Mapcore Identify code. Note that these only conflict when the alias entered is all uppercase.
    restrictedAliases: string[] = ['BEGIN OFFSET', 'BEGIN SEGMENT', 'COUNTY', 'CTY_CODE', 'END OFFSET', 'END SEGMENT', 'JURIS', 'JURISDICTION', 'LONGITUDE', 'LATITUDE', 'OBJECTID', 'PROJECT ID', 'ROUTE', 'SEG_BGN', 'SEG_END', 'ST_RT_NO'];

    constructor(
        private readonly api: Api,
        @Inject('authenticationService') protected readonly authService: IAuthenticationService,
        private readonly layerService: LayerService) {
    }

    async initialize() {
        if (!this.initialized) {
            this.startOver();
            this.authService.GetCurrentUser().subscribe(user => {
                this.user = user;
            });
            this.initialized = true;
        }
    }

    async setupLayerEvents(mapView: IMCMapView) {
        const layers = mapView.map.allLayers;
        layers.on('change', (event) => {
            this.onLayerChange(event);
        });
    }

    async onLayerChange(event) {
        if (event && event.added && event.added.length > 0) {
            for (const layer of event.added) {
                const petlLayer = environment.layers.filter(x => x.name === 'petl')[0];
                const mapcoreProps: IMapcoreProps = layer.mapcoreProps;
                if (mapcoreProps && (mapcoreProps.parentId === petlLayer.id || mapcoreProps.isPETL) && mapcoreProps.columns) {
                    // Replace a PETL layer's column aliases with the aliases setup by the user.
                    if (mapcoreProps.query && mapcoreProps.query.field && mapcoreProps.query.field.toLowerCase() === 'project_no') {
                        // TODO: This runs even if the query is > or < or whatever. Should either not run or needs to run the actual query
                        const projectNumber = mapcoreProps.query.value;
                        const urlParams = ['petl', 'projects', projectNumber.toLowerCase()];
                        const petlProject = await this.petlHttpRequest<IProject>(urlParams, { method: 'GET' }, 'Retreive PETL Project Info');
                        const columnAliases = petlProject.columnAliases;
                        const newColumnList: ILayerColumn[] = [];
                        for (const column of mapcoreProps.columns) {
                            let keepColumnInList = true;
                            const petlColumnAliasesFound = columnAliases.filter(x => x.columnName.toLowerCase() === column.name.toLowerCase());
                            if (petlColumnAliasesFound.length) {
                                {
                                    const columnAlias = petlColumnAliasesFound[0];
                                    column.alias = columnAlias.aliasName;
                                    keepColumnInList = columnAlias.isUsed;
                                }
                            }
                            if (keepColumnInList) {

                                newColumnList.push(column);
                            }
                        }
                        mapcoreProps.columns = newColumnList;
                    }
                }
            }
        }
    }

    async addProjectToMap(project: IProject) {
        const existingProjectLayers = this.layerService.mapLayers.mapImage.filter(layer => {
            let layerMatches = false;
            if (layer.mapcoreProps && layer.mapcoreProps.query) {
                layerMatches = this.queryMatches(layer.mapcoreProps.query, project.id.toString());
            }
            return layerMatches;
        });
        if (existingProjectLayers.length) {
            this.layerService.refreshLayers();
        } else {
            const layerSettings = this.getProjectLayerSettings(project);
            layerSettings.zoomToLayer = this.zoomToLayer;
            this.layerService.addMapcoreLayersToMap([layerSettings]);
        }
    }

    private queryMatches(query: IQuery, projectId: string) {
        let retVal = false;
        if (query.field && (query.field.toLowerCase() === 'project_no' && query.value.toString() === projectId)) {
            retVal = true;
        } else if (query.subqueries) {
            for (const sq of query.subqueries) {
                if (this.queryMatches(sq, projectId)) {
                    retVal = true;
                    break;
                }
            }
        }
        return retVal;
    }

    getProjectLayerSettings(project: IProject): ILayerSettings {
        return {
            id: environment.layers.find(x => x.name === 'petl').id,
            query: { field: 'project_no', operator: '=', value: project.id.toString() },
            name: project.description,
            isPETL: true
        };
    }

    async getProjectList(updateLoading: boolean = true): Promise<IProject[]> {
        const urlParams = ['petl', 'projects-by-user'];
        const projectList = await this.petlHttpRequest<IProject[]>(urlParams, { method: 'GET' }, 'Retreive Projects', updateLoading);
        this.projectList = projectList;
        return projectList;
    }

    async renewProject(project: IProject) {
        const urlParams = ['petl', 'projects', 'renew', project.id.toString()];
        return await this.petlHttpRequest<IProject[]>(urlParams, { method: 'GET' });
    }

    async deleteProject(project: IProject = null) {
        if (!project) {
            project = this.project;
        }
        const urlParams = ['petl', 'projects', project.id.toString()];
        return await this.petlHttpRequest<IProject[]>(urlParams, { method: 'DELETE' });
    }

    async processFileDrop(ev: DragEvent) {
        ev.preventDefault();
        if (this.isValidFileDrop(ev)) {
            const file = ev.dataTransfer.files[0];
            this.sendFile(file);
        }
    }

    async sendFile(inputFile: File) {
        this.loading = true;
        this.changeView('processor');
        this.errorMessage = null;
        this.loading = true;
        const fileName = inputFile.name;
        const extension = this.getFileExtension(fileName);
        const extensionsAllowed = ['.txt', '.csv', '.xls', '.xlsx'];
        if (!extension || extensionsAllowed.indexOf(extension.toLowerCase()) < 0) {
            return;
        }
        if (!this.visible) {
            this.resetVariables();
        }
        const file = inputFile;
        this.fileName = fileName;
        this.fileDropped = true;
        this.visible = true;
        this.errorMessage = '';
        this.showRepeatFileNameIndicator = false;
        this.showRepeatFileHeaderIndicator = false;
        this.columnData = null;

        const urlParams = [];
        urlParams.push('petl/filepreprocess');
        urlParams.push(this.fileId);
        urlParams.push(this.fileName);
        urlParams.push(this.sheetNumber);
        urlParams.push(this.startingRowNumber || 0);

        const preprocessResult = await this.petlHttpRequest<IFilePreprocessResult>(urlParams, { file }, 'Uploading file', false);
        const result = await this.setFilePreProcessResults(preprocessResult);
        this.fileUploaded.emit(preprocessResult);
        return result;
    }

    async sendProcessForm(input: any) {
        this.changeView('validate');
        this.errorMessage = null;
        this.project = input.project;
        this.validationSubmitted = true;
        this.promoteErrorOccurred = false;
        this.errorMessage = '';
        const keepPreviousScenarios = input.append || input.advanced;

        const urlParams = [];
        urlParams.push('petl/fileprocess/columnmapping');
        urlParams.push(this.project.id);
        if (input.scenario.id) {
            urlParams.push(input.scenario.id);
        } else {
            urlParams.push(0);
        }
        urlParams.push(input.scenario.description);
        urlParams.push(this.fileId);
        urlParams.push(this.fileName);
        urlParams.push(this.fileExtension);
        urlParams.push(this.sheetNumber);
        urlParams.push(this.startingRowNumber || 0);
        urlParams.push(keepPreviousScenarios);
        this.project.stagePointCount = null;
        this.project.stagePointCount = null;
        const fileProcessResult = await this.petlHttpRequest<IFileProcessResult>(urlParams, { method: 'POST', body: this.columnMapping }, 'Processing Mapping');
        this.showResultsStats = true;
        this.validationResults = fileProcessResult;
        this.validationReport = fileProcessResult.report;
        this.project = fileProcessResult.project;
        return fileProcessResult;
    }

    async addOrUpdateProject() {
        this.project.userId = this.user.id;
        if (this.project.id) {
            return await this.updateProject(this.project);
        } else {
            return await this.addProject(this.project);
        }
    }

    async getProject(projectId: number) {
        const urlParams = ['petl/projects', projectId.toString()];
        const newProject = await this.petlHttpRequest<IProject>(urlParams, { method: 'GET' }, 'Retrieving Project');
        return newProject;
    }

    async addProject(projectToCreate) {
        const url = 'petl/projects/add';
        const newProject = await this.petlHttpRequest<IProject>([url], { method: 'PUT', body: projectToCreate }, 'Adding new Project');
        this.project = newProject;
        return newProject;
    }

    async updateProject(projectToUpdate: IProject) {
        const urlParams = ['petl/projects', projectToUpdate.id.toString()];
        const updatedProject = await this.petlHttpRequest<IProject>(urlParams, { method: 'POST', body: projectToUpdate }, 'Updating Project');
        return updatedProject;
    }

    async redefineStartingRowNumberInternal(isNewSheet: boolean) {
        this.errorMessage = null;
        const uploadInfo = this.getUploadInfo();
        if (isNewSheet) {
            this.columnData = null;
        }
        const urlParams = [];
        urlParams.push('petl/redefine-starting-row-number');
        const preprocessResult = await this.petlHttpRequest<IFilePreprocessResult>(urlParams, { method: 'POST', body: uploadInfo }, 'Setting a new Start Row Number');
        this.setNewStartingRowNumberResults(preprocessResult, isNewSheet);
        return preprocessResult;
    }

    async promoteData(project: IProject) {
        this.changeView('promoted');
        this.errorMessage = null;
        const runPromoteInBatchMode = !this.promoteErrorOccurred;
        const urlParams = ['petl', 'fileprocess', 'promotedata', project.id.toString(), runPromoteInBatchMode.toString()];
        this.project.pointCount = null;
        this.project.lineCount = null;
        this.project = await this.petlHttpRequest<IProject>(urlParams, { method: 'GET' }, 'Promoting Data');
        this.complete.emit(this.project);
        return project;
    }

    async petlHttpRequest<T>(urlParams: string[], config: IHttp2RequestConfig, whatIsHappening: string = '', updateLoading: boolean = true): Promise<T> {
        if (updateLoading) {
            this.loading = true;
        }
        try {
            return await this.api.http2.request<T>(urlParams.join('/'), config);
        }
        catch (e) {
            this.setErrorMessage(`Error While ${whatIsHappening}`);
            this.loading = false;
            throw e;
        }
        finally {
            if (updateLoading) {
                this.loading = false;
            }
        }
    }

    async resetAutomatedColumnMapping() {
        this.startingRowNumber = null;
        return await this.redefineStartingRowNumberInternal(true);
    }

    async redefineStartingRowNumber() {
        return await this.redefineStartingRowNumberInternal(false);
    }

    startOver() {
        this.loading = false;
        this.resetVariables();
        this.changeView('start');
    }

    goBackToMapping() {
        this.changeView('processor');
        this.validationSubmitted = false;
        this.showResultsStats = false;
        this.promoteDataSubmitted = false;
        this.promoteDataFinished = false;
        this.errorMessage = null;
    }

    isValidFileDrop(dragEvent: DragEvent): boolean {
        if (dragEvent.dataTransfer.files && dragEvent.dataTransfer.files.length) {
            const file = dragEvent.dataTransfer.files[0];
            const validFileExtensions = ['.txt', '.csv', '.xls', '.xlsx'];
            for (const extension of validFileExtensions) {
                if (file.name.toLocaleLowerCase().endsWith(extension)) {
                    return true;
                }
            }
        }
        return false;
    }

    getUploadInfo() {
        const uploadInfo: IUploadInfo = {};
        uploadInfo.userId = this.user.id;
        uploadInfo.fileId = this.fileId;
        uploadInfo.fileName = this.fileName;
        uploadInfo.sheetNumber = this.sheetNumber;
        uploadInfo.startingRowNumber = this.startingRowNumber || 0;
        uploadInfo.ignorePreviousColumnMappings = this.ignorePreviousColumnMappings;
        return uploadInfo;
    }

    resetAliases() {
        if (!this.project || !this.project.columnAliases || !this.results || !this.results.unusedDatabaseFields) {
            return;
        }

        for (const databaseField of this.results.unusedDatabaseFields) {
            this.resetAlias(databaseField);
        }
        for (const databaseField of this.columnData) {
            this.resetAlias(databaseField);
        }
    }

    resetAlias(databaseField) {
        if (databaseField) {
            databaseField.aliasName = null;
        }
    }

    updateColumnAliasesFromProject() {
        if (!this.project || !this.project.columnAliases) {
            return;
        }
        for (const columnAlias of this.project.columnAliases) {
            for (const cd of this.columnData) {
                const databaseField = cd.databaseField;
                this.setAliasName(databaseField, columnAlias);
            }
        }
    }

    setAliasName(databaseField, columnAlias) {
        if (databaseField) {
            if (columnAlias.columnName.toLowerCase() === databaseField.databaseName) {
                databaseField.aliasName = columnAlias.aliasName;
            }
        }
    }

    resetVariables() {
        this.columnData = null;
        this.columnHeadings = null;
        this.columnHeadingsValidated = null;
        this.columnMapping = null;
        this.errorMessage = null;
        this.fileDropped = false;
        this.fileExtension = null;
        this.fileId = 0;
        this.fileSample = null;
        this.ignorePreviousColumnMappings = false;
        this.project = null;
        this.promoteDataFinished = false;
        this.promoteDataSubmitted = false;
        this.results = null;
        this.sheetNumber = 0;
        this.showColumnMapping = false;
        this.showRepeatFileHeaderIndicator = false;
        this.showRepeatFileNameIndicator = false;
        this.showResultsStats = false;
        this.startingRowNumber = null;
        this.validationReport = null;
        this.validationResults = null;
        this.validationSubmitted = false;
    }

    setFilePreProcessResults(filePreprocessResult: IFilePreprocessResult) {
        this.preProcessResult = filePreprocessResult;
        this.results = filePreprocessResult;
        this.fileId = filePreprocessResult.fileId;
        this.startingRowNumber = filePreprocessResult.startingRowNumber;
        this.sheetNumber = filePreprocessResult.sheetNumber;
        this.sheetCount = filePreprocessResult.sheetCount;
        this.fileExtension = filePreprocessResult.fileExtension;
        this.fileSample = filePreprocessResult.fileSample;
        this.columnMapping = filePreprocessResult.columnMapping;
        this.showRepeatFileNameIndicator = filePreprocessResult.containsPreviousMapping;
        this.resultContainedPreviousMapping = filePreprocessResult.containsPreviousMapping;
        this.mappingsFromServerAltered = false;
        if (!this.showRepeatFileNameIndicator) {
            const previousColumnHeadingsValidatedString = this.columnHeadingsValidated !== null ? this.columnHeadingsValidated.toString() : '';
            this.columnHeadings = filePreprocessResult.columnHeadings;
            const newColumnHeadingsString = this.columnHeadings.toString();
            this.showRepeatFileHeaderIndicator = previousColumnHeadingsValidatedString === newColumnHeadingsString;
        }
        this.columnData = filePreprocessResult.columnData;
        this.showColumnMapping = true;
    }

    setNewStartingRowNumberResults(result, newSheet) {
        this.columnHeadings = result.columnHeadings;
        this.fileSample = result.fileSample;
        if (newSheet) {
            this.results = result;
            this.columnData = result.columnData;
            this.mappingsFromServerAltered = false;
            this.resultContainedPreviousMapping = result.containsPreviousMapping;
            this.startingRowNumber = result.startingRowNumber;
        } else if (this.columnData) {
            for (const [index, cd] of this.columnData.entries()) {
                cd.nativeHeader = '';
                if (cd && result.columnData.length > index) {
                    cd.nativeHeader = result.columnData[index].nativeHeader;
                }
            }
        }
    }

    changeView(viewName: string) {
        if (!viewName) {
            if (this.previousView) {
                viewName = this.previousView;
            } else {
                viewName = 'start';
            }
        }
        this.showHelp = false;
        if (this.nonNormalViews.indexOf(this.currentView) < 0 && this.currentView !== viewName) {
            this.previousView = this.currentView;
        }
        this.currentView = viewName;
        this.currentViewChange.next(this.currentView);
    }

    toggleView(viewName: string) {
        if (this.currentView === viewName) {
            this.changeView(this.previousView);
        } else {
            this.changeView(viewName);
        }
    }

    close() {
        this.petlClose.emit(true);
    }

    setErrorMessage(errorMessage, data: any = null) {
        this.errorMessage = errorMessage;
        if (data && data.status && data.status.message) {
            this.errorMessage = `${this.errorMessage}: ${data.status.message}`;
        }
        this.loading = false;
    }

    getFileExtension(fileName: string = null) {
        if (!fileName) {
            fileName = this.fileName;
        }
        return fileName.substr(fileName.lastIndexOf('.'));
    }

}
